import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorToMessage } from "@/lib/apiClient";
import { PageHeader } from "@/components/DataTableParts";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import Modal from "@/components/Modal";

const NEW_UNIT = () => ({
  _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  // V1.8 — item_type governs which fields the line editor renders and how
  // the workflow processes the line on Receive Stock.
  item_type: "laptop",
  brand: "", model: "", processor_generation: "",
  // V1.4 — brand entry is a dropdown of preset options with an "Others (Please Specify)"
  // fallback that reveals a text input. brand_kind is UI-only state, not persisted
  // (we only send `brand` to the backend, unchanged DB contract).
  brand_kind: "",
  ram_gb: 8, storage_gb: 256, storage_type: "SSD", screen_size_inch: 14.0,
  // V1.1: replaces cosmetic_grade
  condition: "unpainted",
  // V1.1: replaces free-text processor
  processor_kind: "intel_core_i5", processor_specify: "",
  // V1.1: replaces charger_included / battery_present — all unchecked by default
  has_charger: false, has_battery: false, has_power_cable: false, has_touchscreen: false,
  unit_cost: "", quantity: 1, remarks: "",
  // V2.4 Commit 2B1 — RAM Purchase Screen redesign. These fields are UI
  // state; on receive we PUT them to /passports/{id}/ram-spec for every
  // newly-created RAM passport (via saveRamSpecs below).
  ram_module_type: "SO_DIMM",
  ram_technology: "DDR4",
  ram_technology_notes: "",
  ram_capacity_choice: "8", // uses ram_gb; "OTHER" reveals a specify input
  ram_capacity_notes: "",
  ram_manufacturer: "",
  ram_part_number: "",
  // V2.5 — RAM Frequency selector (immediately after Technology on the form).
  // Value is stored as the numeric MHz string; "OTHER" reveals a specify input.
  ram_frequency: "3200",
  ram_frequency_notes: "",
  // V2.6 — SSD Purchase Screen redesign. UI state only; synthesized into
  // brand/model in buildLine so backend schema stays untouched.
  ssd_manufacturer: "",
  ssd_technology: "SATA",
  ssd_technology_notes: "",
  ssd_form_factor: "2.5\"",
  ssd_form_factor_notes: "",
  ssd_capacity_choice: "256 GB",
  ssd_capacity_notes: "",
});

// V1.8 — Item Type options. Each type follows a different downstream workflow;
// see backend workflow.py `/receive` for the exact transition matrix.
const ITEM_TYPE_OPTIONS = [
  { v: "laptop",  l: "Laptop" },
  { v: "battery", l: "Battery" },
  { v: "ram",     l: "RAM" },
  { v: "ssd",     l: "SSD" },
  { v: "adapter", l: "Adapter" },
  { v: "other",   l: "Other" },
];
const isLaptopType = (t) => (t || "laptop") === "laptop";

// V1.4 — Preset options for the Brand / RAM / Storage dropdowns. Any value
// not in these lists (e.g. loaded from an existing draft) is treated as
// "Others" for Brand and preserves its value for RAM/Storage.
const BRAND_PRESETS = ["Lenovo", "Dell", "HP"];
const RAM_OPTIONS = [4, 8, 16, 32];
// V2.1 — "No RAM / N/A" is a first-class option (represented as 0). Some
// laptops are procured without RAM installed; operators need to record that
// state accurately without leaving the field blank.
const RAM_NO_VALUE = 0;
const STORAGE_OPTIONS = [
  { v: 128,  l: "128 GB" },
  { v: 256,  l: "256 GB" },
  { v: 512,  l: "512 GB" },
  { v: 1024, l: "1 TB" },
  { v: 2048, l: "2 TB" },
];
// V2.1 — mirror of RAM_NO_VALUE for the Storage/SSD selector.
const STORAGE_NO_VALUE = 0;

// V2.5 — RAM Frequency options (MHz). Rendered as a required dropdown for
// item_type='ram'. "OTHER" reveals a free-text "Specify Frequency" input.
const FREQUENCY_OPTIONS = [
  "1066", "1333", "1600", "1866", "2133", "2400", "2666", "2933",
  "3200", "3600", "4800", "5200", "5600", "6000", "6400",
];

// V2.5 — Derive the `brand` and `model` strings that the backend schema
// requires (min_length=1) for a RAM purchase line. The RAM form no longer
// exposes Brand/Model inputs; instead we synthesize them from the RAM spec
// so the payload stays valid without any backend change.
const ramDerived = (u) => {
  const tech = u.ram_technology === "OTHER"
    ? (u.ram_technology_notes || "").trim()
    : (u.ram_technology || "");
  const freq = u.ram_frequency === "OTHER"
    ? (u.ram_frequency_notes || "").trim()
    : (u.ram_frequency ? `${u.ram_frequency} MHz` : "");
  const cap = u.ram_gb ? `${u.ram_gb}GB` : "";
  const mod = u.ram_module_type === "DIMM" ? "DIMM" : "SO-DIMM";
  const modelParts = [tech, freq, cap, mod].filter(Boolean);
  return {
    brand: (u.ram_manufacturer || "").trim() || "RAM",
    model: modelParts.join(" ") || "RAM Module",
  };
};

// V2.6 — SSD purchase-form option lists.
const SSD_TECH_OPTIONS = ["SATA", "NVMe PCIe", "mSATA", "M.2 SATA", "OTHER"];
const SSD_FORM_OPTIONS = ["2.5\"", "M.2 2242", "M.2 2260", "M.2 2280", "mSATA", "OTHER"];
const SSD_CAPACITY_OPTIONS = [
  "120 GB", "128 GB", "240 GB", "250 GB", "256 GB",
  "480 GB", "500 GB", "512 GB", "1 TB", "2 TB", "4 TB", "OTHER",
];

// V2.6 — Synthesize brand/model for an SSD line (schema requires min_length=1).
// The SSD form exposes a manual "Manufacturer" input and derives everything
// else from Technology / Form Factor / Capacity.
const ssdDerived = (u) => {
  const tech = u.ssd_technology === "OTHER"
    ? (u.ssd_technology_notes || "").trim()
    : (u.ssd_technology || "");
  const form = u.ssd_form_factor === "OTHER"
    ? (u.ssd_form_factor_notes || "").trim()
    : (u.ssd_form_factor || "");
  const cap = u.ssd_capacity_choice === "OTHER"
    ? (u.ssd_capacity_notes || "").trim()
    : (u.ssd_capacity_choice || "");
  const modelParts = [tech, form, cap].filter(Boolean);
  return {
    brand: (u.ssd_manufacturer || "").trim() || "SSD",
    model: modelParts.join(" ") || "SSD",
  };
};

const todayISO = () => new Date().toISOString().slice(0, 10);

// V1.1 Phase 2 — build the line payload used by both Auto Save and Submit.
// V1.8 — for non-laptop item types we send only the fields that make sense
// (brand/model/qty/cost/remarks). Laptop-specific config fields are omitted
// so the backend uses their sensible defaults.
const buildLine = (u) => {
  const laptop = isLaptopType(u.item_type);
  const isRam = u.item_type === "ram";
  const isSsd = u.item_type === "ssd";
  // V2.5 / V2.6 — RAM and SSD forms hide Brand/Model; synthesize them from
  // their spec fields so backend schema's min_length=1 requirement holds.
  const brand = isRam ? ramDerived(u).brand
              : isSsd ? ssdDerived(u).brand
              : u.brand;
  const model = isRam ? ramDerived(u).model
              : isSsd ? ssdDerived(u).model
              : u.model;
  const base = {
    item_type: u.item_type || "laptop",
    brand, model,
    unit_cost: String(u.unit_cost || 0),
    quantity: Math.max(1, Number(u.quantity) || 1),
    remarks: u.remarks || null,
  };
  if (!laptop) return base;
  return {
    ...base,
    processor_generation: u.processor_generation || null,
    // V2.1 — preserve 0 ("No RAM" / "No SSD") through the payload builder.
    // The classic ``Number(x) || null`` pattern was collapsing a legitimate
    // 0 into null; use an explicit "empty-or-not-a-number" guard instead.
    ram_gb: (u.ram_gb === "" || u.ram_gb == null || Number.isNaN(Number(u.ram_gb)))
      ? null : Number(u.ram_gb),
    storage_gb: (u.storage_gb === "" || u.storage_gb == null || Number.isNaN(Number(u.storage_gb)))
      ? null : Number(u.storage_gb),
    storage_type: u.storage_type || null,
    screen_size_inch: u.screen_size_inch ? String(u.screen_size_inch) : null,
    condition: u.condition || "unpainted",
    processor_kind: u.processor_kind || "intel_core_i5",
    processor_specify: u.processor_kind === "others" ? (u.processor_specify || "").trim() : null,
    has_charger: !!u.has_charger, has_battery: !!u.has_battery,
    has_power_cable: !!u.has_power_cable, has_touchscreen: !!u.has_touchscreen,
  };
};

// V1.1 Phase 2 — minimum viable payload for the first Auto Save (create draft).
const canAutoSave = (header, units) => {
  if (!header.vendor_id) return false;
  if (!header.purchase_date) return false;
  // V2.5 — RAM rows never fill Brand/Model in the UI; they're synthesized in
  // buildLine. Treat a RAM row as autosave-ready when it has Technology,
  // Frequency and a positive cost.
  return units.some((u) => {
    if (Number(u.unit_cost) <= 0) return false;
    if (u.item_type === "ram") {
      return !!u.ram_technology && !!u.ram_frequency;
    }
    if (u.item_type === "ssd") {
      return !!u.ssd_technology && !!u.ssd_form_factor && !!u.ssd_capacity_choice;
    }
    return u.brand && u.model;
  });
};

export default function NewPurchasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialDraftId = searchParams.get("draftId") || null;
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [invoiceWarn, setInvoiceWarn] = useState({ open: false, msg: "" });

  const [header, setHeader] = useState({
    purchase_date: todayISO(),
    vendor_id: "",
    vendor_warranty_days: 90,
    invoice_no: "",
    invoice_date: todayISO(),
    purchase_amount: 0, freight: 0, other_charges: 0, gst_amount: 0, total_amount: 0,
    remarks: "",
  });
  const [units, setUnits] = useState([NEW_UNIT()]);
  const [invoiceFile, setInvoiceFile] = useState(null);
  // V1.2 — status of the purchase we're editing (draft vs pending-receiving).
  // Used to change the primary CTA label from "Save & Submit" to "Save Changes"
  // when the purchase has already been submitted and is awaiting receipt.
  const [loadedStatus, setLoadedStatus] = useState(null);

  // V1.1 Phase 2 — Draft & Auto Save state
  const [draftId, setDraftId] = useState(initialDraftId);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [cancelModal, setCancelModal] = useState(false);
  const [draftLoading, setDraftLoading] = useState(!!initialDraftId);
  const autosaveTimer = useRef(null);
  const inFlight = useRef(false);
  const dirtyRef = useRef(false);

  const loadVendors = useCallback(async () => {
    try {
      const r = await api.get("/vendors", { params: { page_size: 200, is_active: true } });
      setVendors(r.data.items);
    } catch (err) { if (process.env.NODE_ENV === "development") console.warn("vendor load", err?.message); }
  }, []);
  useEffect(() => { loadVendors(); }, [loadVendors]);

  // V1.1 Phase 2 — Load existing draft on mount when ?draftId=... present
  useEffect(() => {
    if (!initialDraftId) return;
    (async () => {
      try {
        const r = await api.get(`/purchases/${initialDraftId}`);
        const d = r.data;
        setHeader({
          purchase_date: d.purchase_date, vendor_id: d.vendor_id,
          vendor_warranty_days: d.vendor_warranty_days ?? 90,
          invoice_no: d.invoice_no || "", invoice_date: d.invoice_date || todayISO(),
          purchase_amount: d.purchase_amount ?? 0, freight: d.freight ?? 0,
          other_charges: d.other_charges ?? 0, gst_amount: d.gst_amount ?? 0,
          total_amount: d.total_amount ?? 0, remarks: d.remarks || "",
        });
        setLoadedStatus(d.status || null);
        // V1.2 — /purchases/:id now returns d.lines[] (raw purchase_line rows).
        // Rehydrate them so both DRAFT and PENDING_RECEIVING editing works.
        // Fallback to d.units[] (legacy) if lines missing.
        const dl = Array.isArray(d.lines) ? d.lines : [];
        if (dl.length > 0) {
          // V1.8 — carry item_type through the rehydrated line so the editor
          // still shows the correct fields for non-laptop rows.
          setUnits(dl.map((l) => ({ ...NEW_UNIT(), ...l, item_type: l.item_type || "laptop", quantity: l.quantity || 1, unit_cost: l.unit_cost ?? "" })));
        }
      } catch (err) {
        setError(errorToMessage(err));
      } finally {
        setDraftLoading(false);
      }
    })();
  }, [initialDraftId]);

  const setUnit = (i, patch) => { dirtyRef.current = true; setUnits((prev) => prev.map((u, idx) => (idx === i ? { ...u, ...patch } : u))); };
  const addUnit = () => { dirtyRef.current = true; setUnits((prev) => [...prev, NEW_UNIT()]); };
  const removeUnit = (i) => { dirtyRef.current = true; setUnits((prev) => prev.filter((_, idx) => idx !== i)); };
  const setHeaderPatch = (patch) => { dirtyRef.current = true; setHeader((h) => ({ ...h, ...patch })); };

  // V1.1 Phase 2 — Auto Save (debounced ~2s)
  const persistDraft = useCallback(async () => {
    if (inFlight.current) return;
    if (!canAutoSave(header, units)) { setSaveStatus("idle"); return; }
    inFlight.current = true; setSaveStatus("saving"); setError("");
    try {
      const linesP = units.map(buildLine);
      if (!draftId) {
        const payload = { ...header, total_amount: Number(header.total_amount) || Number(header.purchase_amount || 0) + Number(header.freight || 0) + Number(header.other_charges || 0) + Number(header.gst_amount || 0), lines: linesP };
        const r = await api.post("/purchases", payload);
        setDraftId(r.data.id);
      } else {
        await api.patch(`/purchases/${draftId}`, { ...header, total_amount: Number(header.total_amount) || Number(header.purchase_amount || 0) + Number(header.freight || 0) + Number(header.other_charges || 0) + Number(header.gst_amount || 0), lines: linesP, confirm_duplicate_invoice: true });
      }
      setSaveStatus("saved"); setLastSavedAt(new Date()); dirtyRef.current = false;
    } catch (err) {
      setSaveStatus("error");
      if (process.env.NODE_ENV === "development") console.warn("autosave", err?.message);
    } finally { inFlight.current = false; }
  }, [header, units, draftId]);

  // Debounce on any change
  useEffect(() => {
    if (draftLoading) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { persistDraft(); }, 2000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [header, units, draftLoading, persistDraft]);

  // Save on tab close / refresh via sendBeacon (best-effort)
  useEffect(() => {
    const handler = (e) => {
      if (!dirtyRef.current || !draftId) return;
      try {
        const url = (process.env.REACT_APP_BACKEND_URL || window.location.origin) + `/api/purchases/${draftId}`;
        const body = JSON.stringify({ ...header, lines: units.map(buildLine), confirm_duplicate_invoice: true });
        // sendBeacon doesn't support PATCH — best-effort blocking XHR fallback:
        const xhr = new XMLHttpRequest();
        xhr.open("PATCH", url, false); // sync
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${localStorage.getItem("access_token") || ""}`);
        xhr.send(body);
      } catch {
        // Intentional no-op: best-effort save fired from `beforeunload`. The
        // page is unloading, so we can't surface an error to the user and
        // there's no meaningful recovery path. Any exception (network, XHR
        // blocked by unload, missing token) is safe to swallow here.
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [header, units, draftId]);

  // Auto-compute total if user hasn't touched it
  const computedTotal = useMemo(() =>
    Number(header.purchase_amount || 0) + Number(header.freight || 0) + Number(header.other_charges || 0) + Number(header.gst_amount || 0),
    [header.purchase_amount, header.freight, header.other_charges, header.gst_amount]);

  const submitCore = async (confirmDupInvoice) => {
    setSaving(true); setError("");
    try {
      const payload = {
        ...header,
        total_amount: Number(header.total_amount) || computedTotal,
        lines: units.map(buildLine),
        confirm_duplicate_invoice: !!confirmDupInvoice,
      };
      // V1.1 Phase 2 — if we already have a draft (autosaved), PATCH-then-submit;
      // otherwise create + submit in one flow (original behaviour).
      let purchaseId = draftId;
      if (purchaseId) {
        await api.patch(`/purchases/${purchaseId}`, payload);
      } else {
        const r = await api.post("/purchases", payload);
        purchaseId = r.data.id;
        setDraftId(purchaseId);
      }
      if (invoiceFile) {
        const fd = new FormData();
        fd.append("file", invoiceFile);
        await api.post(`/purchases/${purchaseId}/invoice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      // V1.2 — skip re-submit when editing an already-submitted purchase
      // (PENDING_RECEIVING). Submit endpoint is idempotent but calling it
      // is misleading; PATCH already persisted the edit.
      if (loadedStatus !== "PENDING_RECEIVING") {
        await api.post(`/purchases/${purchaseId}/submit`);
      }
      navigate(`/admin/purchases/${purchaseId}`);
    } catch (err) {
      const code = err?.response?.data?.detail?.error;
      const msg = errorToMessage(err);
      if (code === "duplicate_invoice_warning" && !confirmDupInvoice) {
        setInvoiceWarn({ open: true, msg });
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = (e) => { e.preventDefault(); submitCore(false); };
  const confirmAndSubmit = async () => { setInvoiceWarn({ open: false, msg: "" }); await submitCore(true); };

  // V1.1 Phase 2 — Cancel behaviour: prompt if draft exists.
  const handleCancelClick = () => {
    if (draftId) { setCancelModal(true); }
    else { navigate("/admin/purchases"); }
  };
  const handleKeepDraft = () => { setCancelModal(false); navigate("/admin/purchases"); };
  const handleDeleteDraft = async () => {
    try { await api.delete(`/purchases/${draftId}`); } catch (err) { setError(errorToMessage(err)); return; }
    setCancelModal(false); navigate("/admin/purchases");
  };

  return (
    <div>
      <PageHeader
        title={loadedStatus === "PENDING_RECEIVING" ? "Edit Purchase (Pending Receiving)" : (initialDraftId ? "Edit Draft Purchase" : "New Purchase")}
        subtitle={loadedStatus === "PENDING_RECEIVING"
          ? "Editing is permitted until Receive Stock is completed. Header and item details can be updated."
          : "Enter each laptop type and quantity. Warehouse scans serial numbers later during receiving — Digital Passports are created at that step."} />
      {/* V1.1 Phase 2 — Auto Save indicator (top-right, non-blocking) */}
      <div className="mb-3 flex justify-end text-xs" data-testid="autosave-indicator">
        {saveStatus === "saving" && (<span className="inline-flex items-center gap-1.5 text-slate-500"><Loader2 className="animate-spin" size={12}/>Saving…</span>)}
        {saveStatus === "saved" && (<span className="text-emerald-700">✓ Draft saved{lastSavedAt ? ` · Last saved: ${lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</span>)}
        {saveStatus === "error" && (<span className="text-rose-600">Auto-save failed — will retry on next change</span>)}
        {saveStatus === "idle" && !draftId && (<span className="text-slate-400">Not yet saved — enter vendor + at least one item to auto-save as draft</span>)}
      </div>
      {error && <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm" data-testid="form-error">{error}</div>}

      <form onSubmit={onSubmit} className="space-y-6" data-testid="purchase-form">
        {/* Header */}
        <section className="bg-white border border-slate-200 rounded-md p-4 space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Purchase Header</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Purchase Date *"><input required type="date" className="input" value={header.purchase_date} onChange={(e) => setHeader({ ...header, purchase_date: e.target.value })} data-testid="field-purchase-date"/></Field>
            <Field label="Vendor *">
              <select required className="input" value={header.vendor_id} onChange={(e) => setHeader({ ...header, vendor_id: e.target.value })} data-testid="field-vendor">
                <option value="">— Select vendor —</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </Field>
            <Field label="Vendor Warranty (days)"><input type="number" min={0} max={3650} className="input" value={header.vendor_warranty_days} onChange={(e) => setHeader({ ...header, vendor_warranty_days: Number(e.target.value) })}/></Field>
            <Field label="Invoice No"><input className="input" value={header.invoice_no} onChange={(e) => setHeader({ ...header, invoice_no: e.target.value })} data-testid="field-invoice-no"/></Field>
            <Field label="Invoice Date"><input type="date" className="input" value={header.invoice_date} onChange={(e) => setHeader({ ...header, invoice_date: e.target.value })}/></Field>
            <Field label="Invoice PDF / Image">
              <input type="file" accept="application/pdf,image/*" className="input py-1" onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)} data-testid="field-invoice-file"/>
            </Field>
          </div>
          <div className="grid grid-cols-5 gap-3">
            <Field label="Purchase Amount"><Money value={header.purchase_amount} onChange={(v) => setHeader({ ...header, purchase_amount: v })}/></Field>
            <Field label="Freight"><Money value={header.freight} onChange={(v) => setHeader({ ...header, freight: v })}/></Field>
            <Field label="Other Charges"><Money value={header.other_charges} onChange={(v) => setHeader({ ...header, other_charges: v })}/></Field>
            <Field label="GST"><Money value={header.gst_amount} onChange={(v) => setHeader({ ...header, gst_amount: v })}/></Field>
            <Field label={`Total (calc ₹${computedTotal.toFixed(2)})`}><Money value={header.total_amount || computedTotal} onChange={(v) => setHeader({ ...header, total_amount: v })} testId="field-total"/></Field>
          </div>
          <Field label="Remarks"><textarea rows={2} className="input" value={header.remarks} onChange={(e) => setHeader({ ...header, remarks: e.target.value })}/></Field>
        </section>

        {/* Line Items */}
        <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Line Items ({units.length})</h3>
            <button type="button" onClick={addUnit} data-testid="btn-add-unit"
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-800 px-2 py-1 text-xs">
              <Plus size={12}/> Add Item
            </button>
          </div>
          <div className="space-y-2">
            {units.map((u, i) => (
              <div key={u._uid} className="border border-slate-200 rounded-md p-3 grid grid-cols-6 gap-2 text-sm" data-testid={`unit-${i}`}>
                <Field label="Item Type *">
                  <select required className="input" value={u.item_type || "laptop"}
                    onChange={(e) => setUnit(i, { item_type: e.target.value })}
                    data-testid={`unit-${i}-item-type`}>
                    {ITEM_TYPE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </Field>
                {u.item_type !== "ram" && u.item_type !== "ssd" && (<>
                <Field label="Brand *">
                  <select required className="input"
                    value={u.brand_kind || (BRAND_PRESETS.includes(u.brand) ? u.brand : (u.brand ? "others" : ""))}
                    onChange={(e) => {
                      const v = e.target.value;
                      setUnit(i, v === "others"
                        ? { brand_kind: "others", brand: (u.brand_kind === "others" ? u.brand : "") }
                        : { brand_kind: v, brand: v });
                    }}
                    data-testid={`unit-${i}-brand-kind`}>
                    <option value="">— Select —</option>
                    {BRAND_PRESETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    <option value="others">Others (Please Specify)</option>
                  </select>
                </Field>
                <Field label="Model *"><input required className="input" value={u.model} onChange={(e) => setUnit(i, { model: e.target.value })} data-testid={`unit-${i}-model`}/></Field>
                </>)}
                {isLaptopType(u.item_type) && (<>
                <Field label="Processor">
                  <select className="input" value={u.processor_kind}
                    onChange={(e) => setUnit(i, { processor_kind: e.target.value, ...(e.target.value !== "others" ? { processor_specify: "" } : {}) })}
                    data-testid={`unit-${i}-processor-kind`}>
                    <option value="intel_core_i5">Intel Core i5</option>
                    <option value="intel_core_i7">Intel Core i7</option>
                    <option value="intel_core_i3">Intel Core i3</option>
                    <option value="amd">AMD</option>
                    <option value="ryzen">Ryzen</option>
                    <option value="others">Others (Please Specify)</option>
                  </select>
                </Field>
                <Field label="Gen">
                  <select className="input" value={u.processor_generation || ""} onChange={(e) => setUnit(i, { processor_generation: e.target.value })} data-testid={`unit-${i}-generation`}>
                    <option value="">— Select —</option>
                    {["1st Gen","2nd Gen","3rd Gen","4th Gen","5th Gen","6th Gen","7th Gen","8th Gen","9th Gen","10th Gen","11th Gen","12th Gen","13th Gen","14th Gen"].map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </Field>
                <Field label="RAM (GB)">
                  <select className="input" value={String(u.ram_gb ?? "")}
                    onChange={(e) => setUnit(i, { ram_gb: Number(e.target.value) })}
                    data-testid={`unit-${i}-ram`}>
                    {RAM_OPTIONS.map((v) => <option key={v} value={v}>{v} GB</option>)}
                    {/* V2.1 — "No RAM / N/A" for barebones-purchase units. */}
                    <option value={RAM_NO_VALUE}>No RAM / N/A</option>
                    {!RAM_OPTIONS.includes(Number(u.ram_gb)) && Number(u.ram_gb) !== RAM_NO_VALUE && u.ram_gb !== "" && u.ram_gb != null && (
                      <option value={String(u.ram_gb)}>{u.ram_gb} GB</option>
                    )}
                  </select>
                </Field>
                <Field label="Storage (GB)">
                  <select className="input" value={String(u.storage_gb ?? "")}
                    onChange={(e) => setUnit(i, { storage_gb: Number(e.target.value) })}
                    data-testid={`unit-${i}-storage`}>
                    {STORAGE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    {/* V2.1 — "No SSD / N/A" for barebones-purchase units. */}
                    <option value={STORAGE_NO_VALUE}>No SSD / N/A</option>
                    {u.storage_gb != null && u.storage_gb !== "" && Number(u.storage_gb) !== STORAGE_NO_VALUE && !STORAGE_OPTIONS.some((o) => o.v === Number(u.storage_gb)) && (
                      <option value={String(u.storage_gb)}>{u.storage_gb} GB</option>
                    )}
                  </select>
                </Field>
                <Field label="Storage Type">
                  <select className="input" value={u.storage_type} onChange={(e) => setUnit(i, { storage_type: e.target.value })}>
                    <option value="SSD">SSD</option><option value="NVMe">NVMe</option><option value="HDD">HDD</option><option value="EMMC">EMMC</option>
                  </select>
                </Field>
                <Field label="Screen (in)"><input type="number" step="0.1" min={5} max={30} className="input" value={u.screen_size_inch} onChange={(e) => setUnit(i, { screen_size_inch: e.target.value })}/></Field>
                </>)}
                <Field label="Quantity *"><input required type="number" min={1} max={999} className="input" value={u.quantity} onChange={(e) => setUnit(i, { quantity: e.target.value })} data-testid={`unit-${i}-quantity`}/></Field>
                {/* V2.4 Commit 2B1 — RAM Purchase Screen redesign.
                    Renders full RAM spec fields when item_type='ram'.
                    Values are captured client-side and (a) posted to
                    passport_ram_spec after receive, (b) stored in ram_gb
                    so existing reports/search keep working. */}
                {u.item_type === "ram" && (
                  <>
                    <Field label="Module Type *">
                      <select className="input" value={u.ram_module_type}
                              onChange={(e) => setUnit(i, { ram_module_type: e.target.value })}
                              data-testid={`unit-${i}-ram-module-type`}>
                        <option value="SO_DIMM">SO-DIMM (Laptop)</option>
                        <option value="DIMM">DIMM (Desktop)</option>
                      </select>
                    </Field>
                    <Field label="Technology *">
                      <select className="input" value={u.ram_technology}
                              onChange={(e) => setUnit(i, { ram_technology: e.target.value })}
                              data-testid={`unit-${i}-ram-tech`}>
                        <option value="DDR3">DDR3</option>
                        <option value="DDR3L">DDR3L</option>
                        <option value="DDR4">DDR4</option>
                        <option value="DDR5">DDR5</option>
                        <option value="OTHER">Others</option>
                      </select>
                    </Field>
                    {u.ram_technology === "OTHER" && (
                      <Field label="Specify Technology *">
                        <input className="input" required
                               value={u.ram_technology_notes}
                               onChange={(e) => setUnit(i, { ram_technology_notes: e.target.value })}
                               data-testid={`unit-${i}-ram-tech-notes`} />
                      </Field>
                    )}
                    {/* V2.5 — Frequency: required dropdown immediately after Technology. */}
                    <Field label="Frequency *">
                      <select required className="input" value={u.ram_frequency}
                              onChange={(e) => setUnit(i, { ram_frequency: e.target.value })}
                              data-testid={`unit-${i}-ram-frequency`}>
                        {FREQUENCY_OPTIONS.map((f) => (
                          <option key={f} value={f}>{f} MHz</option>
                        ))}
                        <option value="OTHER">Others</option>
                      </select>
                    </Field>
                    {u.ram_frequency === "OTHER" && (
                      <Field label="Specify Frequency *">
                        <input className="input" required
                               value={u.ram_frequency_notes}
                               onChange={(e) => setUnit(i, { ram_frequency_notes: e.target.value })}
                               data-testid={`unit-${i}-ram-frequency-notes`}
                               placeholder="e.g. 7200 MHz" />
                      </Field>
                    )}
                    <Field label="Capacity *">
                      <select className="input" value={u.ram_capacity_choice}
                              onChange={(e) => {
                                const v = e.target.value;
                                setUnit(i, {
                                  ram_capacity_choice: v,
                                  ram_gb: v === "OTHER" ? u.ram_gb : Number(v),
                                });
                              }}
                              data-testid={`unit-${i}-ram-capacity`}>
                        <option value="4">4 GB</option>
                        <option value="8">8 GB</option>
                        <option value="16">16 GB</option>
                        <option value="32">32 GB</option>
                        <option value="64">64 GB</option>
                        <option value="OTHER">Others</option>
                      </select>
                    </Field>
                    {u.ram_capacity_choice === "OTHER" && (
                      <Field label="Specify Capacity (GB) *">
                        <input type="number" min={0} className="input" required
                               value={u.ram_gb}
                               onChange={(e) => setUnit(i, { ram_gb: Number(e.target.value || 0) })}
                               data-testid={`unit-${i}-ram-capacity-specify`} />
                      </Field>
                    )}
                    <Field label="Manufacturer">
                      <input className="input" value={u.ram_manufacturer}
                             onChange={(e) => setUnit(i, { ram_manufacturer: e.target.value })}
                             data-testid={`unit-${i}-ram-mfr`}
                             placeholder="Optional" />
                    </Field>
                    <Field label="Part Number">
                      <input className="input" value={u.ram_part_number}
                             onChange={(e) => setUnit(i, { ram_part_number: e.target.value })}
                             data-testid={`unit-${i}-ram-pn`}
                             placeholder="Optional" />
                    </Field>
                  </>
                )}
                {/* V2.6 — SSD Purchase Screen redesign.
                    Order: Manufacturer, Technology, Form Factor, Capacity.
                    Quantity / Cost / Remarks are rendered by the shared row. */}
                {u.item_type === "ssd" && (
                  <>
                    <Field label="Manufacturer">
                      <input className="input" value={u.ssd_manufacturer}
                             onChange={(e) => setUnit(i, { ssd_manufacturer: e.target.value })}
                             data-testid={`unit-${i}-ssd-manufacturer`}
                             placeholder="Samsung, Western Digital, Crucial, Kingston, Seagate, etc." />
                    </Field>
                    <Field label="Technology *">
                      <select required className="input" value={u.ssd_technology}
                              onChange={(e) => setUnit(i, { ssd_technology: e.target.value })}
                              data-testid={`unit-${i}-ssd-technology`}>
                        {SSD_TECH_OPTIONS.map((t) => (
                          <option key={t} value={t}>{t === "OTHER" ? "Others" : t}</option>
                        ))}
                      </select>
                    </Field>
                    {u.ssd_technology === "OTHER" && (
                      <Field label="Specify Technology *">
                        <input className="input" required
                               value={u.ssd_technology_notes}
                               onChange={(e) => setUnit(i, { ssd_technology_notes: e.target.value })}
                               data-testid={`unit-${i}-ssd-technology-notes`} />
                      </Field>
                    )}
                    <Field label="Form Factor *">
                      <select required className="input" value={u.ssd_form_factor}
                              onChange={(e) => setUnit(i, { ssd_form_factor: e.target.value })}
                              data-testid={`unit-${i}-ssd-form-factor`}>
                        {SSD_FORM_OPTIONS.map((f) => (
                          <option key={f} value={f}>{f === "OTHER" ? "Others" : f}</option>
                        ))}
                      </select>
                    </Field>
                    {u.ssd_form_factor === "OTHER" && (
                      <Field label="Specify Form Factor *">
                        <input className="input" required
                               value={u.ssd_form_factor_notes}
                               onChange={(e) => setUnit(i, { ssd_form_factor_notes: e.target.value })}
                               data-testid={`unit-${i}-ssd-form-factor-notes`} />
                      </Field>
                    )}
                    <Field label="Capacity *">
                      <select required className="input" value={u.ssd_capacity_choice}
                              onChange={(e) => setUnit(i, { ssd_capacity_choice: e.target.value })}
                              data-testid={`unit-${i}-ssd-capacity`}>
                        {SSD_CAPACITY_OPTIONS.map((c) => (
                          <option key={c} value={c}>{c === "OTHER" ? "Others" : c}</option>
                        ))}
                      </select>
                    </Field>
                    {u.ssd_capacity_choice === "OTHER" && (
                      <Field label="Specify Capacity *">
                        <input className="input" required
                               value={u.ssd_capacity_notes}
                               onChange={(e) => setUnit(i, { ssd_capacity_notes: e.target.value })}
                               data-testid={`unit-${i}-ssd-capacity-notes`}
                               placeholder="e.g. 8 TB" />
                      </Field>
                    )}
                  </>
                )}
                {isLaptopType(u.item_type) && (
                <Field label="Condition">
                  <select className="input" value={u.condition} onChange={(e) => setUnit(i, { condition: e.target.value })} data-testid={`unit-${i}-condition`}>
                    <option value="painted">Painted</option>
                    <option value="unpainted">Unpainted</option>
                    <option value="require_paint">Require Paint</option>
                  </select>
                </Field>
                )}
                <Field label="Cost / unit *"><Money value={u.unit_cost} onChange={(v) => setUnit(i, { unit_cost: v })} testId={`unit-${i}-cost`}/></Field>
                <div className="flex flex-col justify-between">
                  {isLaptopType(u.item_type) && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-700 mt-4">
                      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={u.has_charger} onChange={(e) => setUnit(i, { has_charger: e.target.checked })} data-testid={`unit-${i}-has-charger`}/>Charger</label>
                      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={u.has_battery} onChange={(e) => setUnit(i, { has_battery: e.target.checked })} data-testid={`unit-${i}-has-battery`}/>Battery</label>
                      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={u.has_power_cable} onChange={(e) => setUnit(i, { has_power_cable: e.target.checked })} data-testid={`unit-${i}-has-power-cable`}/>Power Cable</label>
                      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={u.has_touchscreen} onChange={(e) => setUnit(i, { has_touchscreen: e.target.checked })} data-testid={`unit-${i}-has-touchscreen`}/>Touchscreen</label>
                    </div>
                  )}
                  {units.length > 1 && (
                    <button type="button" onClick={() => removeUnit(i)} data-testid={`unit-${i}-remove`}
                      className="mt-2 inline-flex items-center gap-1 self-end rounded-md border border-rose-200 hover:bg-rose-50 text-rose-700 px-2 py-1 text-xs">
                      <Trash2 size={12}/> Remove
                    </button>
                  )}
                </div>
                {isLaptopType(u.item_type) && u.processor_kind === "others" && (
                  <div className="col-span-6">
                    <Field label="Processor (Specify) *">
                      <input required className="input"
                        value={u.processor_specify}
                        onChange={(e) => setUnit(i, { processor_specify: e.target.value })}
                        placeholder="e.g. Intel Xeon, Intel Pentium, Apple M2, Snapdragon"
                        data-testid={`unit-${i}-processor-specify`}
                        maxLength={200}/>
                    </Field>
                  </div>
                )}
                {(u.brand_kind === "others"
                    || (u.brand && !BRAND_PRESETS.includes(u.brand) && u.brand_kind !== ""
                        && !BRAND_PRESETS.includes(u.brand_kind))) && (
                  <div className="col-span-6">
                    <Field label="Brand (Specify) *">
                      <input required className="input"
                        value={u.brand}
                        onChange={(e) => setUnit(i, { brand: e.target.value, brand_kind: "others" })}
                        placeholder="Enter brand name"
                        data-testid={`unit-${i}-brand`}
                        maxLength={80}/>
                    </Field>
                  </div>
                )}
                <div className="col-span-6">
                  <Field label="Remarks"><input className="input" value={u.remarks} onChange={(e) => setUnit(i, { remarks: e.target.value })}/></Field>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex justify-end gap-2">
          <button type="button" onClick={handleCancelClick} data-testid="btn-cancel-draft" className="px-4 py-2 rounded-md border border-slate-300 text-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="btn-submit-purchase"
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
            {loadedStatus === "PENDING_RECEIVING" ? " Save Changes" : " Save & Submit"}
          </button>
        </div>
      </form>

      {/* Duplicate invoice warning */}
      <Modal open={invoiceWarn.open} onClose={() => setInvoiceWarn({ open: false, msg: "" })} title="Duplicate Invoice Number" size="md">
        <div className="space-y-4">
          <div className="text-sm text-slate-700">{invoiceWarn.msg}</div>
          <div className="flex justify-end gap-2">
            <button className="px-3 py-1.5 text-sm rounded-md border border-slate-300" onClick={() => setInvoiceWarn({ open: false, msg: "" })}>Cancel</button>
            <button onClick={confirmAndSubmit} data-testid="btn-confirm-dup-invoice"
              className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white">Continue Anyway</button>
          </div>
        </div>
      </Modal>

      {/* V1.1 Phase 2 — Cancel-with-draft modal */}
      <Modal open={cancelModal} onClose={() => setCancelModal(false)} title="Do you want to keep this draft?" size="md">
        <div className="space-y-4" data-testid="cancel-draft-modal">
          <div className="text-sm text-slate-700">You have unsaved edits in a Draft. Would you like to keep the draft for later, or discard it?</div>
          <div className="flex justify-end gap-2">
            <button className="px-3 py-1.5 text-sm rounded-md border border-slate-300" onClick={() => setCancelModal(false)} data-testid="btn-continue-editing">Continue Editing</button>
            <button className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-slate-700 bg-white" onClick={handleKeepDraft} data-testid="btn-keep-draft">Keep Draft &amp; Leave</button>
            <button className="px-3 py-1.5 text-sm rounded-md bg-rose-600 text-white" onClick={handleDeleteDraft} data-testid="btn-delete-draft">Delete Draft</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs text-slate-600 mb-1">{label}</label>{children}</div>;
}

function Money({ value, onChange, testId }) {
  return (
    <div className="flex">
      <span className="inline-flex items-center px-2 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-600 text-sm">₹</span>
      <input type="number" step="0.01" min={0} className="input rounded-l-none" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}/>
    </div>
  );
}
